import React, { useContext } from 'react';

import { Button } from '@sololearnorg/sol';
import { Container, i18n } from '../../../../symphony';

import {
  AssistanceTypes,
  ContentType,
  SlAISolutionsContext,
} from '../../global-constants';
import { SlActions } from '../../services/ActionsService/sl-actions';
import {
  ActionType,
  GetSolutionClickSource,
  RequestType,
} from '../../global-interface';
import { SlDataService } from '../../services/sl-data.service';
import { AISolutionRating } from './AISolutionRating';
import { AISolutionTab } from './AISolutionTab';
import { AISolutionLoading } from './AISolutionLoading';
import { KodieContext } from '../../services/context';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { SlRichText } from '../../../../shared/public/SlLearnEngine/SlRichText/SlRichText';
import { SlCodeSnippet } from '../../../../shared/public/SlLearnEngine/SlCodeSnippet/SlCodeSnippet';
import { CodeAssistantConfigType, CodeSnippetLanguage } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';

interface IAISolutionProps {
  solutionData: CodeAssistantConfigType['actions'][number];
  backToCodeClickHandler: () => void;
}

export function AISolution({
  solutionData,
  backToCodeClickHandler,
}: IAISolutionProps) {
  const { context } = useContext(KodieContext);
  const { actions$ } = Container.take(SlAISolutionsContext, SlActions);
  const {
    aiSolutionsLoading$: aiSolutionsLoading,
    aiSolutionsData$: aiSolutionsData,
    aiSolutionsIsOpenedState$: aiSolutionsIsOpenedState,
    aiSolutionsErrors$: aiSolutionsErrors,
    questionData$: questionData,
  } = useContainerData(SlAISolutionsContext, SlDataService, [
    'aiSolutionsLoading$',
    'aiSolutionsData$',
    'aiSolutionsErrors$',
    'aiSolutionsIsOpenedState$',
    'questionData$',
  ]);

  const solutionRetryClickHandler = (
    actionId: number,
    isBackToCode = false,
    isFromAskAgain = false,
  ) => {
    if (isBackToCode) {
      backToCodeClickHandler();
      return;
    }

    actions$.next({
      type: ActionType.OnAISolutionClick,
      requestType: RequestType.Parallel,
      data: {
        actionId,
        getSolutionClickSource: isFromAskAgain
          ? GetSolutionClickSource.AskAgain
          : GetSolutionClickSource.TryAgain,
        solutionContext: context,
      },
    });
  };

  const getRetryButton = () => (aiSolutionsErrors[solutionData.action] ? (
    <Button
      buttonType="primary"
      className="ai-solutions__main-section__solution__content__ask-again-button"
      onClick={() => {
        solutionRetryClickHandler(
          solutionData.action,
          aiSolutionsErrors[solutionData.action]
            .isBackToCodeButtonVisible,
        );
      }}
    >
      {aiSolutionsErrors[solutionData.action]
        .isBackToCodeButtonVisible
        ? i18n.t('web-kodie.back-to-code-button')
        : i18n.t('web-kodie.try-again-button')}
    </Button>
  ) : (
    <Button
      buttonType="secondary"
      className="ai-solutions__main-section__solution__content__ask-again-button"
      onClick={() => {
        solutionRetryClickHandler(solutionData.action, false, true);
      }}
    >
      {i18n.t('web-kodie.ask-again-button')}
    </Button>
  ));

  const onCopyhandler = (actionId: number) => {
    actions$.next({
      type: ActionType.OnAISolutionResponseCopy,
      requestType: RequestType.Parallel,
      data: {
        solutionContext: context,
        actionId,
      },
    });
  };

  if (
    [
      AssistanceTypes.AlternativeLessonExplanation,
      AssistanceTypes.RealLifeExamples,
    ].includes(solutionData.action)
    && !!questionData
  ) {
    return null;
  }

  if (solutionData.action === AssistanceTypes.QuestionHint && !questionData) {
    return null;
  }

  return (
    <div
      className="ai-solutions__main-section__solution"
      key={solutionData.action}
    >
      <AISolutionTab solutionData={solutionData} />
      {aiSolutionsIsOpenedState[solutionData.action] && (
        <div
          className={`ai-solutions__main-section__solution__content ${aiSolutionsLoading[solutionData.action] ? 'loading' : ''
            }`}
        >
          {aiSolutionsLoading[solutionData.action] ? (
            <AISolutionLoading />
          ) : (
            <>
              {!!aiSolutionsData[solutionData.action] && (
                <div
                  className="ai-solutions__main-section__solution__content__response"
                  onCopy={() => {
                    onCopyhandler(solutionData.action);
                  }}
                >
                  {aiSolutionsData[
                    solutionData.action
                  ]?.body.map((item, index) => (item.type === ContentType.RichText ? (
                    <SlRichText
                      key={index}
                      content={item.content}
                    />
                  ) : (
                    <SlCodeSnippet
                      key={index}
                      content={item.content}
                      language={
                        item.language as unknown as CodeSnippetLanguage
                      }
                      hideCopyButton
                    />
                  )))}
                </div>
              )}
              <div className="ai-solutions__main-section__solution__content__error-message">
                {i18n.t(
                  aiSolutionsErrors[solutionData.action]
                    ?.errorMessage,
                )}
              </div>
              {aiSolutionsData[solutionData.action]
                ?.isSolution ? null : (
                <>
                  {aiSolutionsErrors[solutionData.action]
                    ?.isTryAgainButtonVisible && getRetryButton()}
                  {!!aiSolutionsData[solutionData.action] && (
                    <AISolutionRating
                      id={
                        aiSolutionsData[
                          solutionData.action
                        ].id
                      }
                      solutionType={solutionData.action}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
